import { get } from 'svelte/store';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { app } from '$lib/stores/app/store';
import { getEnvironment } from '$lib/utils/environment';
import { getUserAgentInfo, type UserAgentInfo } from '$lib/utils/user-agent-info';
import { getContinentCode } from '$lib/utils/country-to-continent';
import {
	APP_INFO,
	ENVIRONMENTS,
	LINK_FROM_APP_PLATFORMS,
	LOCAL_STORAGE_KEYS,
} from '$lib/utils/constants';
import type { App as AppNamespace } from '$lib/stores/app/types';
import { type IClientSessionInit } from '$events';
import { Analytics, type EventData } from './client';

type AppMeta = AppNamespace.Meta;
type AppExperiment = AppNamespace.Experiment;
type SessionInitData = IClientSessionInit['data'];

/** `client.session_init` data slot minus `aofl_product` (stamped by `Analytics.log`). */
export type SessionInitDataInput = EventData<'client.session_init'>;

countries.registerLocale(enLocale);

const SESSION_INIT_SAFE_ACTION = 'SessionInit: sub-collector failed';
const { safe, safeAsync } = Analytics.createSafeRunner(SESSION_INIT_SAFE_ACTION);

/**
 * Builds the `client.session_init` data slot. Sub-collectors are
 * wrapped so a flaky source degrades to its empty shape rather than
 * blocking emission. `link_from_app_platform` is sourced from
 * `app.global` (resolved from gateway + query), not `app.meta` as
 * it can change during user sessions and is not an immutable
 * property of the app itself.
 *
 * TODO (nice to have): add a deduplication guard so a second
 * `session_init` within the same tab session is suppressed or
 * flagged.
 * @returns {Promise<SessionInitDataInput>}
 */
export async function collectSessionInitData(): Promise<SessionInitDataInput> {
	const appState = get(app);
	const userAgentInfo = await safeAsync(getUserAgentInfo, EMPTY_USER_AGENT_INFO);

	return {
		active_client_experiments: safe(() => mapExperiments(appState.global.experiments), {}),
		adid: safe(readAdid, undefined),
		app_info: safe(buildAppInfo, EMPTY_APP_INFO),
		device_info: safe(() => buildDeviceInfo(userAgentInfo), EMPTY_DEVICE_INFO),
		feature_flags: safe(() => mapFeatureFlags(appState.global.featureFlags ?? {}), {}),
		geo_info: safe(() => buildGeoInfo(appState.meta), EMPTY_GEO_INFO),
		is_link_from_app: safe(() => appState.global.isLinkFromApp ?? false, false),
		link_from_app_platform: safe(
			() => appState.global.isLinkFromAppPlatform ?? LINK_FROM_APP_PLATFORMS.DEFAULT,
			LINK_FROM_APP_PLATFORMS.DEFAULT
		),
		marketing_name: userAgentInfo.marketingName,
		settings_info: safe(buildSettingsInfo, EMPTY_SETTINGS_INFO),
		udid: safe(readUdid, undefined),
	};
}

/**
 * Narrows the legacy environment string to the analytics enum.
 * @param {string} env Value from `getEnvironment()`.
 * @returns {SessionInitData['app_info']['environment']}
 */
export function mapEnvironment(env: string): SessionInitData['app_info']['environment'] {
	switch (env) {
		case ENVIRONMENTS.PRODUCTION:
			return 'prod';
		case ENVIRONMENTS.STAGING:
			return 'staging';
		case ENVIRONMENTS.DEVELOPMENT:
			return 'dev';
		case ENVIRONMENTS.LOCAL_DEVELOPMENT:
			return 'local';
		default:
			return 'prod';
	}
}

/**
 * Reduces experiments to `{ key: variant }`, dropping unassigned.
 * @param {AppExperiment[]} experiments Assigned experiment rows from the app store.
 * @returns {Record<string, string>}
 */
export function mapExperiments(experiments: AppExperiment[]): Record<string, string> {
	const out: Record<string, string> = {};
	for (const exp of experiments) {
		if (exp.assigned !== false) {
			out[exp.key] = exp.variant;
		}
	}
	return out;
}

/**
 * Coerces the store's feature-flag map to scalar values. Accepts
 * raw scalars or the legacy `{ status, meta }` shape; unknown
 * shapes degrade to `false`.
 * @param {Record<string, unknown>} flags Feature flags from the app store.
 * @returns {Record<string, boolean | string | number>}
 */
export function mapFeatureFlags(
	flags: Record<string, unknown>
): Record<string, boolean | string | number> {
	const out: Record<string, boolean | string | number> = {};
	for (const [key, value] of Object.entries(flags)) {
		out[key] = extractFeatureFlagStatus(value);
	}
	return out;
}

/**
 * Extracts a scalar value from a feature-flag store entry. Raw
 * scalars are passed through unchanged; the legacy `{ status }` object
 * shape is coerced to a boolean; unknown shapes degrade to `false`.
 * @param {unknown} value Raw store value.
 * @returns {boolean | string | number}
 */
function extractFeatureFlagStatus(value: unknown): boolean | string | number {
	if (typeof value === 'boolean' || typeof value === 'string' || typeof value === 'number')
		return value;
	if (value !== null && typeof value === 'object' && 'status' in value) {
		const status = (value as { status?: unknown }).status;
		return status === true || status === 'true';
	}
	return false;
}

/**
 * Builds the `app_info` sub-slot from build defines and environment.
 * @returns {SessionInitData['app_info']}
 */
function buildAppInfo(): SessionInitData['app_info'] {
	return {
		app_build_number: __APP_BUILD_NUMBER__,
		app_name: APP_INFO.APP_NAME,
		app_version: __APP_VERSION__,
		environment: mapEnvironment(getEnvironment()),
		framework: APP_INFO.FRAMEWORK,
		framework_version: __SVELTEKIT_VERSION__,
	};
}

/**
 * Builds the `device_info` sub-slot from navigator, screen, and parsed UA.
 * @param {UserAgentInfo} userAgentInfo Parsed user-agent fields.
 * @returns {SessionInitData['device_info']}
 */
function buildDeviceInfo(userAgentInfo: UserAgentInfo): SessionInitData['device_info'] {
	const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
	return {
		browser_name: userAgentInfo.browserName,
		browser_version: userAgentInfo.browserVersion,
		color_depth: screen.colorDepth,
		cpu_cores: navigator.hardwareConcurrency,
		device_type: userAgentInfo.deviceType,
		is_physical_device: true,
		languages: [...navigator.languages],
		locale: navigator.language,
		max_touch_points: navigator.maxTouchPoints,
		os_name: userAgentInfo.osName,
		os_version: userAgentInfo.osVersion,
		physical_memory: deviceMemory,
		pixel_ratio: window.devicePixelRatio,
		platform: navigator.platform,
		screen_height: screen.height,
		screen_width: screen.width,
		user_agent: navigator.userAgent,
		vendor: navigator.vendor,
		viewport_height: window.innerHeight,
		viewport_width: window.innerWidth,
	};
}

/**
 * Geo cascade: CloudFront (`meta.viewer*`) → `Intl.Locale` →
 * library lookup from the resolved country code.
 * @param {AppMeta} meta CloudFront viewer metadata from the app store.
 * @returns {SessionInitData['geo_info']}
 */
function buildGeoInfo(meta: AppMeta): SessionInitData['geo_info'] {
	const cfCountry = (meta.viewerCountry ?? '').toUpperCase();
	const country = cfCountry || deriveCountryFromLocale();

	return {
		area_code: '',
		city: meta.viewerCity ?? '',
		continent_code: country ? getContinentCode(country) : '',
		country_code: country,
		country_code3: country ? (countries.alpha2ToAlpha3(country) ?? '') : '',
		country_name:
			meta.viewerCountryName || (country ? (countries.getName(country, 'en') ?? '') : ''),
		dma_code: meta.viewerMetroCode ?? '',
		latitude: meta.viewerLatitude ?? '',
		longitude: meta.viewerLongitude ?? '',
		postal_code: meta.viewerPostalCode ?? '',
		region: meta.viewerCountryRegion ?? '',
		time_zone: meta.viewerTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
	};
}

/**
 * Country from browser language preference (not physical location).
 * @returns {string} ISO 3166-1 alpha-2 country code, or empty when unavailable.
 */
function deriveCountryFromLocale(): string {
	try {
		return new Intl.Locale(navigator.language).maximize().region ?? '';
	} catch {
		return '';
	}
}

/**
 * Builds the `settings_info` sub-slot from navigator and Intl APIs.
 *
 * TODO: `settings_info.languages` duplicates `device_info.languages`.
 * Both are kept for schema parity with the legacy AMSL payload; the
 * redundant field should be removed when the schema is versioned.
 * @returns {SessionInitData['settings_info']}
 */
function buildSettingsInfo(): SessionInitData['settings_info'] {
	return {
		languages: [...navigator.languages],
		locale: navigator.language,
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
	};
}

/**
 * Reads the attribution device id from localStorage.
 * @returns {string | undefined}
 */
function readAdid(): string | undefined {
	return localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined;
}

/**
 * Reads the unique device identifier from localStorage.
 * @returns {string | undefined}
 */
function readUdid(): string | undefined {
	return localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined;
}

const EMPTY_USER_AGENT_INFO: UserAgentInfo = {
	browserName: '',
	browserVersion: '',
	deviceType: 'desktop',
	marketingName: '',
	osName: '',
	osVersion: '',
};

const EMPTY_APP_INFO: SessionInitData['app_info'] = {
	app_name: APP_INFO.APP_NAME,
	app_version: '',
};

const EMPTY_DEVICE_INFO: SessionInitData['device_info'] = {
	is_physical_device: true,
};

const EMPTY_GEO_INFO: SessionInitData['geo_info'] = {};

const EMPTY_SETTINGS_INFO: SessionInitData['settings_info'] = {
	locale: '',
};
